import { useSyncExternalStore } from "react";

/**
 * Returns `true` when the current viewport matches the given CSS media query.
 *
 * SSR-safe: returns `false` during server rendering and the very first client
 * paint, then re-renders with the actual value once mounted. This avoids the
 * hydration-mismatch warnings that a `useState + useEffect` implementation
 * would produce when a component renders different content based on viewport.
 *
 * The subscription listens for `change` events on `window.matchMedia(query)`,
 * so the value updates on resize, rotation, and the browser DevTools device-
 * toolbar toggle without polling.
 *
 * Example:
 *   const isNarrow = useMediaQuery("(max-width: 767.99px)");
 */
export function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (notify) => {
      if (typeof window === "undefined") return () => {};
      const mql = window.matchMedia(query);
      mql.addEventListener("change", notify);
      return () => mql.removeEventListener("change", notify);
    },
    () => window.matchMedia(query).matches,
    () => false,
  );
}
